import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";
import { AlertOctagon, CheckCircle2, Loader2, PhoneCall, Info, KeyRound, Unlock } from "lucide-react";
import AdminIncidentsWorkflow from "@/components/admin/AdminIncidentsPanel";

const CANCEL_LABEL = {
  rupture: "Rupture",
  client_injoignable: "Client injoignable",
  adresse_introuvable: "Adresse introuvable",
  autre: "Autre",
};

export default function AdminIncidentsPanel() {
  const { t } = useTranslation();
  const [incidents, setIncidents] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [lockedOrders, setLockedOrders] = useState([]);
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const [inc, canc, locked] = await Promise.all([
        apiClient.get(`/admin/cash-incidents${filter === "all" ? "" : `?status=${filter}`}`),
        apiClient.get("/admin/cancellations?limit=50"),
        apiClient.get("/admin/locked-orders"),
      ]);
      setIncidents(inc.data || []);
      setCancellations(canc.data || []);
      setLockedOrders(locked.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (id) => {
    const note = window.prompt(t("admin.dipNotePrompt"));
    if (!note) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await apiClient.patch(`/admin/cash-incidents/${id}`, { resolution_note: note });
      toast.success(t("admin.dipResolved"));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const unlockOrder = async (orderId) => {
    if (!window.confirm(t("admin.dipUnlockConfirm"))) return;
    setBusy((b) => ({ ...b, [orderId]: true }));
    try {
      await apiClient.post(`/admin/orders/${orderId}/unlock-delivery-code`);
      toast.success(t("admin.dipUnlocked"));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-incidents-panel">
      {/* NEW: ticket-based incident workflow (à traiter / en cours / résolu) */}
      <section>
        <h2 className="font-display text-xl font-semibold flex items-center gap-2 mb-3">
          <AlertOctagon className="w-5 h-5 text-brand-red" />
          Tickets incidents livreur
        </h2>
        <AdminIncidentsWorkflow />
      </section>

      {/* LOCKED DELIVERY CODES */}
      {lockedOrders.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2 mb-3">
            <KeyRound className="w-5 h-5 text-brand-red" />
            Codes de livraison verrouillés <span className="text-mute font-normal text-sm">({lockedOrders.length})</span>
          </h2>
          <div className="space-y-2">
            {lockedOrders.map((o) => (
              <div
                key={o.order_id}
                data-testid={`locked-order-${o.order_id}`}
                className="bg-white border-2 border-brand-red/40 rounded-2xl p-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-mute">
                      Commande #{o.order_id.slice(-6).toUpperCase()} · {formatGNF(o.total)} · {o.zone}
                    </div>
                    <div className="font-semibold text-sm mt-1">{o.restaurant_name}</div>
                    <div className="text-sm text-mute mt-0.5">
                      Livreur : <b>{o.driver_name || "—"}</b>{o.driver_phone ? ` · ${o.driver_phone}` : ""}
                    </div>
                    <div className="text-sm text-mute">Client : {o.phone} · {o.address}</div>
                    <div className="text-xs text-brand-red font-semibold mt-1">
                      {o.delivery_code_attempts || 0}/5 tentatives · code verrouillé
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {o.driver_phone && (
                      <a
                        href={`tel:${o.driver_phone.replace(/[^+0-9]/g, "")}`}
                        className="rounded-full border border-sand hover:border-brand-green px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"
                      >
                        <PhoneCall className="w-3.5 h-3.5" /> Appeler livreur
                      </a>
                    )}
                    <button
                      onClick={() => unlockOrder(o.order_id)}
                      disabled={busy[o.order_id]}
                      data-testid={`unlock-order-${o.order_id}`}
                      className="rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {busy[o.order_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                      Déverrouiller
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CASH INCIDENTS */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-brand-red" />
            Incidents paiement cash <span className="text-mute font-normal text-sm">({incidents.length})</span>
          </h2>
          <div className="flex gap-1">
            {["open", "resolved", "all"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`incidents-filter-${f}`}
                className={`text-xs rounded-full px-3 py-1 border ${filter === f ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
              >
                {f === "open" ? t("admin.dipOpenF") : f === "resolved" ? t("admin.aipResolvedF") : t("admin.dipAllF")}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-mute text-sm py-6 text-center">Chargement…</div>
        ) : incidents.length === 0 ? (
          <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute text-sm">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-brand-green opacity-60" />
            Aucun incident {filter === "open" ? "à traiter" : filter === "resolved" ? "résolu" : ""}.
          </div>
        ) : (
          <div className="space-y-2">
            {incidents.map((i) => (
              <div key={i.incident_id} data-testid={`incident-${i.incident_id}`} className={`bg-white border rounded-2xl p-4 ${i.status === "open" ? "border-brand-red/40" : "border-sand"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-mute">
                      Commande #{i.order_id?.slice(-6).toUpperCase()} · {formatGNF(i.amount)} · {new Date(i.reported_at).toLocaleString("fr-FR")}
                    </div>
                    <div className="font-semibold text-sm mt-1">
                      Livreur : {i.driver_name} <span className="text-mute font-normal">({i.driver_phone || "—"})</span>
                    </div>
                    <div className="text-sm text-mute mt-0.5">Client : {i.client_phone || "—"}</div>
                    <div className="text-sm bg-elevated rounded-xl p-2 mt-2">« {i.comment} »</div>
                    {i.status === "resolved" && i.resolution_note && (
                      <div className="text-xs text-brand-green mt-2 flex items-start gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span><b>{t("admin.aipResolved")}</b> · {i.resolution_note}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {i.status === "open" ? (
                      <>
                        {i.client_phone && (
                          <a href={`tel:${i.client_phone.replace(/[^+0-9]/g, "")}`} className="rounded-full border border-sand hover:border-brand-green px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
                            <PhoneCall className="w-3.5 h-3.5" /> Appeler client
                          </a>
                        )}
                        <button
                          onClick={() => resolve(i.incident_id)}
                          disabled={busy[i.incident_id]}
                          data-testid={`incident-resolve-${i.incident_id}`}
                          className="rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                        >
                          {busy[i.incident_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          Marquer résolu
                        </button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-brand-green font-semibold">{t("admin.aipResolved")}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CANCELLATIONS */}
      <section>
        <h2 className="font-display text-xl font-semibold flex items-center gap-2 mb-3">
          <Info className="w-5 h-5 text-brand-red" />
          Annulations <span className="text-mute font-normal text-sm">({cancellations.length})</span>
        </h2>
        {cancellations.length === 0 ? (
          <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute text-sm">{t("admin.dipNoCancellation")}</div>
        ) : (
          <div className="bg-white border border-sand rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
                  <tr>
                    <th className="text-left px-3 py-2">Commande</th>
                    <th className="text-left px-3 py-2">Restaurant</th>
                    <th className="text-left px-3 py-2">Motif</th>
                    <th className="text-left px-3 py-2">Commentaire</th>
                    <th className="text-left px-3 py-2">Par</th>
                    <th className="text-right px-3 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.map((c) => (
                    <tr key={c.order_id} data-testid={`cancellation-${c.order_id}`} className="border-t border-sand">
                      <td className="px-3 py-2 font-mono text-xs">#{c.order_id.slice(-6).toUpperCase()}</td>
                      <td className="px-3 py-2">{c.restaurant_name}</td>
                      <td className="px-3 py-2">
                        <span className="rounded-full bg-brand-red/10 text-brand-red text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5">
                          {CANCEL_LABEL[c.cancellation?.reason] || c.cancellation?.reason}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-mute text-xs max-w-[240px] truncate">{c.cancellation?.comment || "—"}</td>
                      <td className="px-3 py-2 text-xs text-mute">{c.cancellation?.by_role}</td>
                      <td className="px-3 py-2 text-right text-xs text-mute">{c.cancelled_at ? new Date(c.cancelled_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
